import { OrganizationDto } from "@intelligems/ig-types";

/**
 * Resolve `urlOrPath` to a full URL for the builder preview.
 *
 * When a `demoUrl` is configured the builder is pointed at a store other than
 * the logged-in org, so paths resolve against *that store's origin*; otherwise
 * against the org's own domain. Only the demo URL's origin participates — its
 * path is merely where the preview started, not a prefix every later path
 * hangs off. For "open the demo URL exactly as configured", which is a
 * different question, use `resolveInitialUrl`.
 *
 * The demo URL is passed in (read from redux by the caller) rather than read
 * from storage here — this stays a pure util.
 */
export function resolveFullUrl(
  urlOrPath: string,
  org?: OrganizationDto,
  demoUrl?: string | null
): string {
  if (isAbsoluteHttpUrl(urlOrPath)) return urlOrPath;

  // A malformed demo URL yields no origin and falls back to the org's domain,
  // rather than stranding the preview on an unusable base.
  const baseUrl = demoOriginOf(demoUrl) ?? orgBaseUrl(org);
  if (!baseUrl) return urlOrPath;

  try {
    return new URL(urlOrPath, baseUrl).href;
  } catch {
    return urlOrPath;
  }
}

/**
 * The URL the builder opens at: the configured demo URL exactly as given — it
 * may point at a specific page, which is usually the reason for setting one —
 * or the org's own home page.
 *
 * Deliberately not `resolveFullUrl("/", org, demoUrl)`: that asks "where does
 * `/` live on the previewed store", which is the org's landing page, not the
 * demo URL the merchant typed.
 */
export function resolveInitialUrl(
  org?: OrganizationDto,
  demoUrl?: string | null
): string {
  if (demoUrl) {
    try {
      return new URL(demoUrl).href;
    } catch {
      // Malformed demo URL — fall through to the org's own home page.
    }
  }
  return resolveFullUrl("/", org);
}

function isAbsoluteHttpUrl(value: string): boolean {
  return value.startsWith("http://") || value.startsWith("https://");
}

/** Scheme+host of the demo URL, or undefined when there isn't a usable one. */
function demoOriginOf(demoUrl?: string | null): string | undefined {
  if (!demoUrl) return undefined;
  try {
    return new URL(demoUrl).origin;
  } catch {
    return undefined;
  }
}

/** Base URL of the logged-in org's own store, or undefined when unknown. */
export function orgBaseUrl(org?: OrganizationDto): string | undefined {
  const base =
    org?.domain ?? (org?.shopId ? `${org.shopId}.myshopify.com` : "");
  if (!base) return undefined;
  return isAbsoluteHttpUrl(base) ? base : `https://${base}`;
}
